import { ASSET_TYPES } from "../../../enums/assetTypes.enum";
import { isNotNullOrUndefined, transformManufacturerName } from "../../../shared/utils/utils";
import { Position } from "../../shared/position";
import {
  FaultRef,
  LocationRef,
  CustomerRef,
  TenantRef,
  AtedBy,
  Timestamp,
  Reference,
  DoorRef,
} from "../../shared/refs";
import { Parents } from "./botevoAsset";
type NonUndefined<T> = {
  [P in keyof T]: Exclude<T[P], undefined>;
};
interface BuildingRef {
  id?: string;
  label?: string;
  buildingFloorMapUrl?: string;
}
export interface DoorDTO {
  id?: string | null;
  label: string;
  assetType?: string | null;
  fault?: FaultRef;
  repairStatus?: string | null;
  coreId?: string | null;
  core?: boolean | null;
  severity?: string | null;
  personalDoor?: boolean | null;
  gateHistoryCycle?: number | null;
  activeDate?: number | null;
  tempOffset?: string | null;
  width?: number | null;
  height?: number | null;
  normalOperationThreshold?: number | null;
  hasGuard?: boolean | null;
  status?: string | null;
  loadingBridge?: string | null;
  quickupGate?: string | null;
  isSluiceGate?: boolean | null;
  hasButler?: boolean | null;
  emailEnergyLoss?: boolean | null;
  notes?: string | null;
  position?: Position;
  gateType?: Reference | null;
  gateManufacturer?: string | null;
  gateMake?: string | null;
  brand?: string | null;
  controlManufacturer?: Reference | null;
  controlModel?: Reference | null;
  building?: BuildingRef;
  location?: LocationRef;
  customer?: CustomerRef;
  tenant?: TenantRef;
  gateSystem?: DoorRef;
  gateNumber?: string | null;
  retailerNumber?: string | null;
  expectedEnergyLossPerDay?: number | null;
  expectedEnergyLossPerYear?: number | null;
  timestamp?: Timestamp;
  createdBy?: AtedBy | null;
  updatedBy?: AtedBy | null;
}

export class Door {
  id?: string;
  assetType: string;
  label: string;
  status?: string;
  coreId?: string;
  personalDoor?: boolean;
  gateHistoryCycle?: number;
  activeDate?: number;
  tempOffset?: number;
  expectedEnergyLossPerDay?: number;
  expectedEnergyLossPerYear?: number;
  width?: number;
  height?: number;
  normalOperationThreshold?: number | null;
  hasButler?: boolean;
  hasGuard?: boolean;
  loadingBridge?: string;
  quickupGate?: string;
  isSluiceGate?: boolean;
  emailEnergyLoss?: boolean;
  location?: Reference;
  building?: BuildingRef;
  customer?: Reference;
  gateType?: Reference;
  gateNumber?: string;
  gateSystem?: DoorRef;
  retailerNumber?: string;
  gateManufacturer?: string;
  gateMake?: string;
  controlManufacturer?: Reference;
  controlModel?: Reference;
  buildingFloorMapUrl?: string;
  position?: Position;
  severity?: string;
  core?: boolean;

  constructor(door: DoorDTO) {
    if (door.id) this.id = door.id;
    this.assetType = ASSET_TYPES.DOOR;
    this.label = door.label;
    if (door.status) this.status = door.status;
    if (door.severity) this.severity = door.severity;
    if (door.coreId) this.coreId = door.coreId;
    if (isNotNullOrUndefined(door.expectedEnergyLossPerYear)) {
      this.expectedEnergyLossPerDay =
        door.expectedEnergyLossPerYear !== undefined ? door.expectedEnergyLossPerYear / 365 : undefined;
      this.expectedEnergyLossPerYear = door.expectedEnergyLossPerYear;
    }
    if (isNotNullOrUndefined(door.personalDoor)) this.personalDoor = door.personalDoor;
    if (isNotNullOrUndefined(door.gateHistoryCycle)) this.gateHistoryCycle = door.gateHistoryCycle;
    if (isNotNullOrUndefined(door.activeDate)) this.activeDate = door.activeDate;
    if (isNotNullOrUndefined(door.tempOffset)) this.tempOffset = Number(door.tempOffset);
    if (isNotNullOrUndefined(door.width)) this.width = door.width;
    if (isNotNullOrUndefined(door.height)) this.height = door.height;
    this.normalOperationThreshold = door.normalOperationThreshold || 0;
    if (isNotNullOrUndefined(door.hasGuard)) this.hasGuard = door.hasGuard;
    if (isNotNullOrUndefined(door.hasButler)) this.hasButler = door.hasButler;
    if (isNotNullOrUndefined(door.loadingBridge)) this.loadingBridge = door.loadingBridge;
    if (isNotNullOrUndefined(door.quickupGate)) this.quickupGate = door.quickupGate;
    if (isNotNullOrUndefined(door.isSluiceGate)) this.isSluiceGate = door.isSluiceGate;
    if (isNotNullOrUndefined(door.emailEnergyLoss)) this.emailEnergyLoss = door.emailEnergyLoss;
    if (door.location) this.location = door.location;
    if (door.building) this.building = door.building;
    if (door.customer) this.customer = door.customer;
    if (door.gateType) this.gateType = door.gateType;
    if (door.gateManufacturer) this.gateManufacturer = door.gateManufacturer;
    if (door.gateNumber) this.gateNumber = door.gateNumber;
    if (door.retailerNumber) this.retailerNumber = door.retailerNumber;
    if (door.gateMake) this.gateMake = door.gateMake;
    if (isNotNullOrUndefined(door.core)) this.core = door.core;
    if (door.gateSystem) this.gateSystem = door.gateSystem;
    if (door.controlManufacturer) {
      this.controlManufacturer = {
        label: transformManufacturerName(door.controlManufacturer?.label),
        id: door.controlManufacturer.id,
      };
    }
    if (door.controlModel) this.controlModel = door.controlModel;
    if (door.position) this.position = door.position;
  }
  public set parents(parents: Parents) {
    this.location = parents.location;
    this.customer = parents.customer;
  }
  public get values(): Partial<NonUndefined<DoorDTO>> {
    const entries = Object.entries(this)
      .filter(([value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, value as Exclude<any, undefined>]);

    const values = Object.fromEntries(entries);
    if (values.id) {
      delete values.id;
    }

    if (values.building?.buildingFloorMapUrl) {
      delete values.building.buildingFloorMapUrl;
    }

    if (values.gateSystem) {
      delete values.gateSystem;
    }
    if (values.normalOperationThreshold === 0) {
      values.normalOperationThreshold = null;
    }
    if (values.status) {
      delete values.status;
    }

    if (values.buildingFloorMapUrl) {
      delete values.buildingFloorMapUrl;
    }

    if (values.assetType) {
      delete values.assetType;
    }
    if (values.severity) {
      delete values.severity;
    }
    if (!values.core) {
      delete values.controlManufacturer;
      delete values.controlModel;
    }

    if (!values.position?.latitude && !values.position?.longitude) {
      delete values.position;
    }

    return values;
  }

  public get directParentId() {
    return this.building?.id;
  }

  public set properties(door: DoorDTO) {
    this.label = door.label;
    // if(door.status) this.status = door.status;
    if (door.coreId) this.coreId = door.coreId;
    if (isNotNullOrUndefined(door.personalDoor)) this.personalDoor = door.personalDoor;
    if (isNotNullOrUndefined(door.gateHistoryCycle)) this.gateHistoryCycle = door.gateHistoryCycle;
    if (isNotNullOrUndefined(door.activeDate)) this.activeDate = door.activeDate;
    if (isNotNullOrUndefined(door.tempOffset)) this.tempOffset = Number(door.tempOffset);
    if (isNotNullOrUndefined(door.width)) this.width = door.width;
    if (isNotNullOrUndefined(door.height)) this.height = door.height;
    if (isNotNullOrUndefined(door.normalOperationThreshold))
      this.normalOperationThreshold = door.normalOperationThreshold;
    // this.expectedEnergyLossPerDay = door.expectedEnergyLossPerDay;
    if (isNotNullOrUndefined(door.expectedEnergyLossPerYear))
      this.expectedEnergyLossPerYear = door.expectedEnergyLossPerYear;
    if (isNotNullOrUndefined(door.hasButler)) this.hasButler = door.hasButler;
    if (isNotNullOrUndefined(door.hasGuard)) this.hasGuard = door.hasGuard;
    if (isNotNullOrUndefined(door.loadingBridge)) this.loadingBridge = door.loadingBridge;
    if (isNotNullOrUndefined(door.quickupGate)) this.quickupGate = door.quickupGate;
    if (door.gateNumber) this.gateNumber = door.gateNumber;
    if (door.retailerNumber) this.retailerNumber = door.retailerNumber;
    if (isNotNullOrUndefined(door.isSluiceGate)) this.isSluiceGate = door.isSluiceGate;
    if (isNotNullOrUndefined(door.emailEnergyLoss)) this.emailEnergyLoss = door.emailEnergyLoss;
    if (isNotNullOrUndefined(door.core)) this.core = door.core;
    if (door.location) {
      this.location = door.location;
    }
    if (door.building && door.building) {
      this.building = door.building;
    }
    if (this.customer && door.customer) {
      this.customer = door.customer;
    }
    if (door.gateType) this.gateType = door.gateType;
    if (door.gateManufacturer) this.gateManufacturer = door.gateManufacturer;
    if (door.gateMake) this.gateMake = door.gateMake;
    this.position = door.position;
    if (door.controlManufacturer) {
      this.controlManufacturer = {
        label: transformManufacturerName(door.controlManufacturer?.label),
        id: door.controlManufacturer.id,
      };
    }
    if (door.controlModel) {
      this.controlModel = {
        label: door.controlModel?.label,
        id: door.controlModel.id,
      };
    }
  }
}
